import React, { useEffect, useRef, useState } from 'react'
import { Alert, Col, DatePicker, Form, Input, Modal, Row, Select, Spin } from 'antd'
import { v4 as uuid } from 'uuid'
import fetchAPI from '../claims/accounting/api'

const times = ['09:00 - 11:00', '11:00 - 13:00', '13:00 - 15:00', '15:00 - 17:00', '17:00 - 19:00']

export default function CreateClaimWork({
  open,
  onClose,
  onCreated,
  serviceRecordId,
}: {
  open: boolean
  onClose: () => void
  onCreated: (id: number) => void
  serviceRecordId?: number
}) {
  const [form] = Form.useForm()
  const [users, setUsers] = useState<Array<{ id: number; name: string }>>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const requestId = useRef(uuid())
  const responsible = Form.useWatch('responsible', form)
  const engineers = Form.useWatch('engineers', form) || []
  useEffect(() => {
    if (!open) return
    let active = true
    form.resetFields()
    requestId.current = uuid()
    setError(null)
    setLoading(true)
    fetchAPI('/api/claim-works/create-options')
      .then(result => {
        if (active) setUsers(result.engineers || [])
      })
      .catch(e => {
        if (active) setError(e.message || 'Не удалось загрузить исполнителей')
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [open, form])
  const save = async () => {
    if (saving || loading) return
    let values
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    setSaving(true)
    setError(null)
    try {
      const result = await fetchAPI('/api/claim-works', {
        method: 'POST',
        body: JSON.stringify({
          title: values.title,
          address: values.address,
          date_work_completion: values.date_work_completion.format('YYYY-MM-DD'),
          time_work_completion: values.time_work_completion,
          engineer_ids: [values.responsible, ...(values.engineers || [])],
          comment: values.comment,
          request_id: requestId.current,
          service_record_id: serviceRecordId,
        }),
      })
      onCreated(result.id)
    } catch (e: any) {
      setError(e?.message || 'Не удалось создать работу')
    } finally {
      setSaving(false)
    }
  }
  return (
    <Modal
      title="Создать работу без рекламации"
      open={open}
      onCancel={() => !saving && onClose()}
      onOk={save}
      okText="Создать работу"
      cancelText="Отмена"
      confirmLoading={saving}
      okButtonProps={{ disabled: loading || users.length === 0 }}
      maskClosable={false}
      closable={!saving}
      destroyOnHidden
      width={680}
    >
      <Spin spinning={loading}>
        {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label="Что нужно сделать"
            rules={[{ required: true, whitespace: true, message: 'Опишите работу' }]}
          >
            <Input.TextArea rows={2} maxLength={2000} />
          </Form.Item>
          <Form.Item
            name="address"
            label="Адрес работы"
            rules={[{ required: true, whitespace: true, message: 'Укажите адрес' }]}
          >
            <Input maxLength={2000} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="responsible"
                label="Ответственный"
                rules={[{ required: true, message: 'Выберите ответственного' }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={users
                    .filter(user => !engineers.includes(user.id))
                    .map(user => ({ value: user.id, label: user.name }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="engineers" label="Дополнительные исполнители">
                <Select
                  mode="multiple"
                  optionFilterProp="label"
                  options={users
                    .filter(user => user.id !== responsible)
                    .map(user => ({ value: user.id, label: user.name }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="date_work_completion"
                label="Дата исполнения (план)"
                rules={[{ required: true, message: 'Укажите дату' }]}
              >
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="time_work_completion"
                label="Время исполнения (план)"
                rules={[{ required: true, type: 'array', min: 1, message: 'Выберите время' }]}
              >
                <Select mode="multiple" options={times.map(value => ({ value, label: value }))} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="comment" label="Комментарий">
            <Input.TextArea rows={2} maxLength={2000} />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  )
}
